<script>
import Logo from './Logo.vue';
import PrimaryNavItem from './PrimaryNavItem.vue';
import OptionsMenu from './OptionsMenu.vue';
import AgentDetails from './AgentDetails.vue';
import NotificationBell from './NotificationBell.vue';
import wootConstants from 'dashboard/constants/globals';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { ACCOUNT_EVENTS } from '../../../helper/AnalyticsHelper/events';
import WebphoneMinimized from './WebphoneMinimized.vue';

export default {
  components: {
    Logo,
    PrimaryNavItem,
    OptionsMenu,
    AgentDetails,
    NotificationBell,
    WebphoneMinimized,
  },
  props: {
    isACustomBrandedInstance: {
      type: Boolean,
      default: false,
    },
    logoSource: {
      type: String,
      default: '',
    },
    installationName: {
      type: String,
      default: '',
    },
    accountId: {
      type: Number,
      default: 0,
    },
    menuItems: {
      type: Array,
      default: () => [],
    },
    activeMenuItem: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      helpDocsURL: wootConstants.DOCS_URL,
      showOptionsMenu: false,
    };
  },
  methods: {
    frontendURL,
    toggleOptions() {
      this.showOptionsMenu = !this.showOptionsMenu;
    },
    toggleAccountModal() {
      this.$emit('toggleAccounts');
    },
    toggleSupportChatWindow() {
      window.$chatwoot.toggle();
    },
    openNotificationPanel() {
      this.$track(ACCOUNT_EVENTS.OPENED_NOTIFICATIONS);
      this.$emit('openNotificationPanel');
    },
  },
};
</script>

<template>
  <div class="topnav-bar flex items-center w-full h-16 px-3 gap-1">
    <div class="topnav-bar__glow" />
    <div class="topnav-bar__brand flex items-center gap-2 pr-3 mr-1">
      <Logo
        :source="logoSource"
        :name="installationName"
        :account-id="accountId"
        class="topnav-bar__logo"
      />
      <span class="topnav-bar__name hidden lg:inline">{{
        installationName
      }}</span>
    </div>

    <nav
      class="topnav-bar__links flex items-center gap-1 h-full flex-1 min-w-0 overflow-x-auto"
    >
      <PrimaryNavItem
        v-for="menuItem in menuItems"
        :key="menuItem.toState"
        :icon="menuItem.icon"
        :name="menuItem.label"
        :to="menuItem.toState"
        :is-child-menu-active="menuItem.key === activeMenuItem"
      />
    </nav>

    <div class="topnav-bar__actions flex items-center gap-1 pl-3 ml-1">
      <WebphoneMinimized />
      <PrimaryNavItem
        v-if="!isACustomBrandedInstance"
        icon="book-open-globe"
        name="DOCS"
        icon-only
        open-in-new-page
        :to="helpDocsURL"
      />
      <NotificationBell @openNotificationPanel="openNotificationPanel" />
      <AgentDetails @toggleMenu="toggleOptions" />
      <OptionsMenu
        :show="showOptionsMenu"
        @toggleAccounts="toggleAccountModal"
        @showSupportChatWindow="toggleSupportChatWindow"
        @openKeyShortcutModal="$emit('openKeyShortcutModal')"
        @close="toggleOptions"
      />
    </div>
  </div>
</template>

<style scoped>
/*
 * The topnav is a fixed brand-chrome strip — it intentionally does not
 * react to the app's light/dark theme toggle (see --color-rail-* tokens in
 * _brand-theme.scss). A radial glow + bottom shadow replace the old
 * theme-reactive flat border, giving the bar a "floating layer" feel.
 */
.topnav-bar {
  position: sticky;
  top: 0;
  z-index: 15;
  background-color: rgb(var(--color-rail-bg));
  box-shadow:
    0 1px 0 rgb(0 0 0 / 25%),
    0 4px 24px -12px rgb(0 0 0 / 45%);
}

/*
 * The glow lives in its own absolutely-positioned, overflow-hidden layer
 * instead of on .topnav-bar itself — clipping the *bar* would also clip
 * OptionsMenu (a descendant that intentionally pops out past the bar's own
 * box). This regressed the profile-menu-hidden-behind-sidebar bug once
 * already (see 397b824) for a different reason (stacking context); keep
 * this layer isolated so it can never do that again.
 */
.topnav-bar__glow {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 0;
}

.topnav-bar__glow::before {
  content: '';
  position: absolute;
  top: 50%;
  left: -10%;
  width: 45%;
  height: 260%;
  background: radial-gradient(
    ellipse at center,
    rgb(var(--color-rail-accent) / 16%) 0%,
    rgb(var(--color-rail-accent) / 0%) 70%
  );
  transform: translateY(-50%);
}

.topnav-bar__brand,
.topnav-bar__actions {
  /*
   * Deliberately no `position: relative` here — they're flex items of
   * `.topnav-bar`, so `z-index` already applies without it (flex/grid items
   * establish a stacking context even at `position: static`). Adding it
   * would make this the containing block for OptionsMenu's `absolute`
   * positioning instead of `.topnav-bar` itself, and `top-full` would then
   * resolve against this cluster's own (shorter) height instead of the
   * full bar height, opening the menu mid-bar instead of below it.
   */
  z-index: 1;
  flex-shrink: 0;
}

.topnav-bar__brand {
  border-right: 1px solid rgb(255 255 255 / 10%);
}

.topnav-bar__actions {
  border-left: 1px solid rgb(255 255 255 / 10%);
}

.topnav-bar__links {
  z-index: 1;
  scrollbar-width: none;
}

.topnav-bar__links::-webkit-scrollbar {
  display: none;
}

.topnav-bar__logo {
  border-radius: 0.625rem;
  box-shadow: 0 0 0 1px rgb(255 255 255 / 10%);
  flex-shrink: 0;
}

.topnav-bar__name {
  font-size: 0.8125rem;
  font-weight: 700;
  color: rgb(var(--color-rail-icon));
  white-space: nowrap;
}

[dir='rtl'] .topnav-bar__brand {
  border-right: 0;
  border-left: 1px solid rgb(255 255 255 / 10%);
}

[dir='rtl'] .topnav-bar__actions {
  border-left: 0;
  border-right: 1px solid rgb(255 255 255 / 10%);
}
</style>
